import React, { useState, useEffect } from "react";
import { Header, Segment } from "semantic-ui-react";
import { ConfirmModal } from "../../../components/Shared";
import { GeiFactorLibrary } from "../../../api";
import { useAuth } from "../../../hooks";
import { toast } from "react-toastify";
import "./GEIRevisionCalendar.scss";

const libraryCtrl = new GeiFactorLibrary();

const EMPTY = { month: "", source: "", scope: "", action_required: "", how_to_verify: "", is_critical: false };

export function GEIRevisionCalendar() {
  const { accessToken } = useAuth();
  const [entries,  setEntries]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(null); // null | { mode:'new'|'edit', data }
  const [deleting, setDeleting] = useState(null);
  const [saving,   setSaving]   = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await libraryCtrl.getRevisionCalendar(accessToken);
      if (r.code === 200) setEntries(r.entries);
    } catch { toast.error("Error al cargar el calendario"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [accessToken]); // eslint-disable-line

  const openNew  = () => setModal({ mode: 'new',  data: { ...EMPTY } });
  const openEdit = (e) => setModal({ mode: 'edit', data: { ...e } });

  const handleSave = async () => {
    const { data, mode } = modal;
    if (!data.month.trim() || !data.source.trim() || !data.action_required.trim()) {
      toast.error("Mes, fuente y acción requerida son obligatorios"); return;
    }
    setSaving(true);
    try {
      if (mode === 'new') {
        await libraryCtrl.createRevisionEntry(accessToken, data);
        toast.success("Entrada creada");
      } else {
        await libraryCtrl.updateRevisionEntry(accessToken, data._id, data);
        toast.success("Entrada actualizada");
      }
      setModal(null);
      await load();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await libraryCtrl.deleteRevisionEntry(accessToken, id);
      toast.success("Entrada eliminada");
      setDeleting(null);
      await load();
    } catch { toast.error("Error al eliminar"); }
  };

  const set = (k, v) => setModal(m => ({ ...m, data: { ...m.data, [k]: v } }));

  return (
    <div className="gei-cal">
      <Segment className="gei-cal__title-pill">
        <Header as="h1">Calendario de revision de factores GEI</Header>
      </Segment>

      <div className="gei-cal__toolbar">
        <p className="gei-cal__sub">Que fuente publica en que mes · Responsabilidad Sustemia · Una vez al año</p>
        <button className="obj-add-btn" onClick={openNew} title="Nueva entrada">+</button>
      </div>

      {loading ? (
        <div className="gei-cal__loading">Cargando...</div>
      ) : (
        <div className="gei-cal__table-wrap">
          <table className="gei-cal__table">
            <thead>
              <tr>
                <th>Mes</th>
                <th>Fuente</th>
                <th>Paises / Alcance</th>
                <th>Accion requerida</th>
                <th>Como verificar</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e._id} className={e.is_critical ? 'gei-cal__row--critical' : ''}>
                  <td className="gei-cal__td-month">{e.month}</td>
                  <td className="gei-cal__td-source"><strong>{e.source}</strong></td>
                  <td className="gei-cal__td-scope">{e.scope}</td>
                  <td className="gei-cal__td-action">{e.action_required}</td>
                  <td className="gei-cal__td-url">{e.how_to_verify}</td>
                  <td className="gei-cal__td-actions">
                    <button className="gei-cal__btn-icon" title="Editar" onClick={() => openEdit(e)}>
                      <i className="edit icon" />
                    </button>
                    <button className="gei-cal__btn-icon gei-cal__btn-del" title="Eliminar" onClick={() => setDeleting(e)}>
                      <i className="trash icon" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL: nueva entrada / editar */}
      {modal && (
        <div className="gei-cal__overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="gei-cal__modal">
            <div className="gei-cal__modal-header">
              <p className="gei-cal__modal-title">{modal.mode === 'new' ? 'Nueva entrada' : 'Editar entrada'}</p>
              <p className="gei-cal__modal-sub">Calendario anual de revision GEI</p>
            </div>

            <div className="gei-cal__modal-section">
              <div className="gei-cal__field">
                <label>Mes <span className="req">*</span></label>
                <input type="text" placeholder="Ej: Enero, Marzo-Abr, Variable..."
                  value={modal.data.month} onChange={e => set('month', e.target.value)} />
              </div>
              <div className="gei-cal__field">
                <label>Fuente <span className="req">*</span></label>
                <input type="text" placeholder="Ej: CAMMESA (Argentina), DEFRA (UK)..."
                  value={modal.data.source} onChange={e => set('source', e.target.value)} />
              </div>
              <div className="gei-cal__field">
                <label>Paises / Alcance</label>
                <input type="text" placeholder="Ej: Argentina - electricidad"
                  value={modal.data.scope} onChange={e => set('scope', e.target.value)} />
              </div>
            </div>

            <div className="gei-cal__modal-section">
              <div className="gei-cal__field">
                <label>Accion requerida <span className="req">*</span></label>
                <textarea rows={3} placeholder="Que hay que hacer..."
                  value={modal.data.action_required} onChange={e => set('action_required', e.target.value)} />
              </div>
              <div className="gei-cal__field">
                <label>Como verificar / URL</label>
                <textarea rows={2} placeholder="URL o pasos para verificar..."
                  value={modal.data.how_to_verify} onChange={e => set('how_to_verify', e.target.value)} />
              </div>
              <label className="gei-cal__check-label">
                <input type="checkbox" checked={modal.data.is_critical}
                  onChange={e => set('is_critical', e.target.checked)} />
                Marcar como critico (fila roja)
              </label>
            </div>

            <div className="gei-cal__modal-footer">
              <div className="gei-cal__modal-actions">
                <button className="gei-cal__em-btn gei-cal__em-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Guardando...' : modal.mode === 'new' ? 'Crear entrada' : 'Guardar cambios'}
                </button>
                <button className="gei-cal__em-btn gei-cal__em-btn-cancel" onClick={() => setModal(null)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!deleting}
        title="Eliminar entrada"
        content={deleting ? <>Estas seguro que queres eliminar <strong>{deleting.source}</strong> ({deleting.month})?</> : ''}
        onConfirm={() => handleDelete(deleting._id)}
        onCancel={() => setDeleting(null)}
        danger
      />
    </div>
  );
}
